import { useEffect, useCallback } from "react";
import Win2kButton from "@/components/win2k/Win2kButton";
import { cn } from "@/lib/utils";

interface RoundResult {
  username: string;
  status: string;
  round_score: number;
  number_sum: number;
  has_multiplier: boolean;
  flat_bonus: number;
  flip_seven: boolean;
  new_total: number;
}

export interface RoundResults {
  round: number;
  results: RoundResult[];
}

interface RoundEndSummaryProps {
  roundResults: RoundResults;
  onDismiss: () => void;
}

export default function RoundEndSummary({ roundResults, onDismiss }: RoundEndSummaryProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === "Escape" || e.key === " ") {
        onDismiss();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="win2k-outset bg-[var(--win2k-window-bg)] max-w-sm w-full mx-4">
        <div className="win2k-title-bar px-1 py-0.5 min-h-[22px]">
          <span className="text-xs font-bold">
            Round {roundResults.round} Complete
          </span>
        </div>
        <div className="p-3 space-y-3">
          <div className="space-y-1.5">
            {roundResults.results.map((result) => (
              <div
                key={result.username}
                className={cn(
                  "win2k-inset p-1.5 text-[11px]",
                  result.status === "busted" && "opacity-60"
                )}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-bold">{result.username}</span>
                  {result.status === "busted" ? (
                    <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1 rounded">
                      BUST — 0 pts
                    </span>
                  ) : (
                    <span className="font-mono font-bold text-green-700">
                      +{result.round_score} pts
                    </span>
                  )}
                </div>
                {result.status !== "busted" && (
                  <div className="text-[10px] text-[var(--win2k-shadow)] space-x-2">
                    <span>Cards: {result.number_sum}</span>
                    {result.has_multiplier && <span>&times;2</span>}
                    {result.flat_bonus > 0 && <span>+{result.flat_bonus}</span>}
                    {result.flip_seven && (
                      <span className="text-amber-600 font-bold">+15 FLIP 7</span>
                    )}
                  </div>
                )}
                <div className="text-[10px] text-[var(--win2k-button-text)] mt-0.5">
                  Total: <span className="font-mono font-bold">{result.new_total}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Win2kButton onClick={onDismiss} className="min-w-[100px]" autoFocus>
              Continue
            </Win2kButton>
          </div>
        </div>
      </div>
    </div>
  );
}
